import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  ErrorMessage,
  ModalOpenProcess,
  ModalOpenProcessRef,
  useRunWithFlag,
  useScreenWidth,
} from "@msys/ui";
import { TouchApp as TouchAppIcon } from "@mui/icons-material";
import { Box, IconButton, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useEffectOnce, useLockBodyScroll, useUpdateEffect } from "react-use";
import { PimSearchProductsSorting } from "../../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { ButtonCircleWrapper } from "../../../commons/button/ButtonCircleWrapper.js";
import { ButtonCreate } from "../../../commons/button/Button.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../../commons/hooks/useCollectionView.js";
import { LayoutWithLeftColumn } from "../../../commons/layout/LayoutWithLeftColumn.js";
import { track } from "../../../track.js";
import { SectionTab } from "../../doc-items/SearchProductsAndTemplates.js";
import { SupplierCatalogueValue } from "../../suppliers/SupplierCatalogueSelect.js";
import { useDataGridStateStore } from "../../users/useDataGridStateStore.js";
import { OrganisationSupplierProductFavouriteButton } from "../buttons/OrganisationSupplierProductFavouriteButton.js";
import { ProductPreferenceButton } from "../buttons/ProductPreferenceButton.js";
import {
  isAnyFilterSet,
  ProductFiltersFormValues,
} from "../filters/ProductFilterFields.js";
import { productFiltersDefaultValue } from "../filters/ProductFilters.js";
import { useProductSearchType } from "../filters/useProductSearchTypes.js";
import {
  DEFAULT_PRODUCT_SORTING,
  useCanCreateSupplierCatalogueProduct,
} from "../helper.js";
import { getPreferenceProductType } from "../productPreferences.js";
import { useProductPreferences } from "../useProductPreferences.js";
import {
  ProductAskTypeProcess,
  ProductAskTypeProcessRef,
} from "../modals/ProductAskTypeModal.js";
import {
  ProductCreateModal,
  ProductCreateModalProps,
} from "../modals/ProductCreateModal.js";
import { ProductOverviewModal } from "../modals/ProductOverviewModal.js";
// The search query keeps living next to the modal so its persisted-query id stays stable.
import { useProductSearchModalQuery } from "../modals/ProductSearchModal.generated.js";
import {
  SupplierCatalogueProductCreateModal,
  SupplierCatalogueProductCreateModalProps,
} from "../modals/SupplierCatalogueProductCreateModal.js";
import { ProductSearchItem__ProductSearchResultFragment } from "../Product.generated.js";
import { ProductCategoryTreeChip } from "../ProductCategoryTreeChip.js";
import {
  ProductCategoryTreeSelect,
  useProductCategoryTree,
} from "../ProductCategoryTreeSelect.js";
import { ProductSearchLandingPage } from "../ProductSearchLandingPage.js";
import {
  ChildrenFn,
  ProductSearchListHeader,
} from "../ProductSearchListHeader.js";
import { ProductsListView } from "./ProductsListView.js";

const ALLOWED_VIEWS: CollectionView[] = ["gallery", "list", "table"];
const DEFAULT_PAGE_SIZE = 25;

export interface ProductSearchContentProps {
  initialSearchValue?: string;
  initialFilters?: Partial<ProductFiltersFormValues>;
  /**
   * Omitted where the search is opened to work on the catalogue rather than to pick from it:
   * without it no row offers a selection, because there is nothing for a selection to fill.
   */
  handleProductChoice?: (
    product: Pick<
      ProductSearchItem__ProductSearchResultFragment,
      "articleNumber" | "supplierId"
    >,
    filters: ProductFiltersFormValues
  ) => Promise<void> | void;
  handleProductCreate?: ProductCreateModalProps["handleSubmit"];
  /** Called once this surface is done with, e.g. after a selection was applied. */
  handleClose: () => void;
  canChangeProductType?: boolean;
  initialPageSize?: number;
  pageSizeOptions?: number[];
  allowedViews?: CollectionView[];
  collectionViewPersistenceKey?: string;
  onFiltersChange?: (filters: ProductFiltersFormValues) => void;
  /**
   * Off where the search is driven by filters the caller set and must not drop, e.g. a
   * placeholder: without the landing page there is nothing to go back to.
   */
  enableLandingPage?: boolean;
  /**
   * The search and filter lines bleed into the surrounding surface's own horizontal padding,
   * which only lines up inside the dialog. Off for surfaces without that padding.
   */
  edgeBleed?: boolean;
  /**
   * The product currently assigned to the caller's entity, marked in the results
   * when it is among them. Purely a visual affordance — it does not seed the
   * filters, so the marked row may well be absent from the current page.
   */
  selectedProduct?: { articleNumber: string; supplierId: string } | null;
  /**
   * Keys the preference control to this product type instead of to the filtered one, so the
   * filter stays free to be widened or dropped while the preference target does not move.
   */
  pinnedPreferenceProductTypeId?: string | null;
  /**
   * Set where the search is driven by a product placeholder: preferred articles stay
   * reachable under the placeholder's product type even when classified otherwise.
   */
  includePreferences?: boolean;
}

export const ProductSearchContent = ({
  initialSearchValue = "",
  initialFilters,
  handleProductChoice,
  handleProductCreate,
  handleClose,
  canChangeProductType = true,
  initialPageSize = DEFAULT_PAGE_SIZE,
  pageSizeOptions,
  allowedViews = ALLOWED_VIEWS,
  collectionViewPersistenceKey = "products-list-modal",
  onFiltersChange,
  enableLandingPage = true,
  edgeBleed = true,
  selectedProduct,
  pinnedPreferenceProductTypeId = null,
  includePreferences = false,
}: ProductSearchContentProps) => {
  const { isMinTablet, isMinLargeDesktop } = useScreenWidth();

  const [productOverviewModalProduct, setProductOverviewModalProduct] =
    React.useState<{ articleNumber: string; supplierId: string } | null>(null);

  // Both parts must match: article numbers are only unique within a supplier.
  const isSelectedProduct = React.useCallback(
    (
      product: Pick<
        ProductSearchItem__ProductSearchResultFragment,
        "articleNumber" | "supplierId"
      >
    ) =>
      selectedProduct !== null &&
      selectedProduct !== undefined &&
      product.articleNumber === selectedProduct.articleNumber &&
      product.supplierId === selectedProduct.supplierId,
    [selectedProduct]
  );

  const { productSearchTypeOptions, productSearchType, setProductSearchType } =
    useProductSearchType();

  const [paginationModel, setPaginationModel] = React.useState<{
    page: number;
    pageSize: number;
  }>({ page: 0, pageSize: initialPageSize });

  const [sortModel, setSortModel] = React.useState<PimSearchProductsSorting[]>(
    DEFAULT_PRODUCT_SORTING
  );

  const [searchTerm, setSearchTerm] =
    React.useState<string>(initialSearchValue);

  const initialFilterState = React.useMemo<ProductFiltersFormValues>(
    () => ({
      ...productFiltersDefaultValue,
      ...initialFilters,
    }),
    [initialFilters]
  );
  const [filters, setFilters] =
    React.useState<ProductFiltersFormValues>(initialFilterState);

  React.useEffect(() => {
    onFiltersChange?.(filters);
  }, [filters, onFiltersChange]);

  const productSearchVariables = React.useMemo(
    () => ({
      searchTerm:
        productSearchType === "fulltextSearch" ? searchTerm : undefined,
      specificFieldQueryFields:
        productSearchType !== "fulltextSearch"
          ? [{ field: productSearchType, value: searchTerm }]
          : undefined,
      filters,
      includePreferences,
    }),
    [filters, includePreferences, productSearchType, searchTerm]
  );

  const [section, setSection] = React.useState<SectionTab>(
    !enableLandingPage || Boolean(searchTerm) || isAnyFilterSet(filters)
      ? "results"
      : "landing"
  );

  // automatically switching to results when needed
  useEffectOnce(() => {
    if (Boolean(searchTerm) || isAnyFilterSet(filters)) {
      setSection("results");
    }
  });
  useUpdateEffect(() => {
    if (
      (Boolean(searchTerm) || isAnyFilterSet(filters)) &&
      section === "landing"
    )
      setSection("results");
  }, [searchTerm, filters]);

  const client = useApolloClient();
  const query = useProductSearchModalQuery({
    client,
    variables: {
      ...productSearchVariables,
      limit: paginationModel.pageSize,
      offset: paginationModel.pageSize * paginationModel.page,
      sorting: sortModel,
    },
    skip: section !== "results",
  });

  const resetFilters = () => {
    setFilters(initialFilterState);
  };

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>(
      collectionViewPersistenceKey,
      "list",
      "list"
    );

  const products =
    getDataOrNull(
      (query.data ?? query.previousData)?.pimSearchProducts
    )?.edges.map(e => e.node) ?? [];
  const totalCount =
    getDataOrNull((query.data ?? query.previousData)?.pimSearchProducts)
      ?.totalCount ?? 0;

  const onProductSelect = handleProductChoice
    ? async (product: ProductSearchItem__ProductSearchResultFragment) => {
        track({
          eventName: "AddProduct",
          data: { product, variables: productSearchVariables },
        });
        await handleProductChoice(product, filters);
        handleClose();
      }
    : undefined;

  React.useEffect(() => {
    const totalCount = getDataOrNull(query.data?.pimSearchProducts)?.totalCount;
    if (
      totalCount &&
      totalCount < paginationModel.pageSize * paginationModel.page &&
      paginationModel.page > 0
    ) {
      setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    }
  }, [query.data, paginationModel, setPaginationModel]);

  const [searchEnabled, setSearchEnabled] = React.useState<boolean>(false);
  useLockBodyScroll(searchEnabled);

  const handleBackClick =
    enableLandingPage && section === "results"
      ? () => {
          resetFilters();
          setSection("landing");
        }
      : undefined;

  const handleSearchSubmit = () => {
    if (section === "landing") setSection("results");
  };

  const productCategoryProps = useProductCategoryTree(
    filters.supplierIds?.[0] ?? null,
    filters.supplierProductCategoryKey ?? null
  );

  const showCategoriesSelect = Boolean(
    productCategoryProps.supplier &&
    (productCategoryProps.childCategories.length ||
      productCategoryProps.productCategory)
  );

  const handleCategoryTreeSelect = ({
    supplierId,
    supplierProductCategoryKey,
  }: {
    supplierProductCategoryKey?: string | null;
    supplierId?: string | null;
  }) => {
    const newFilters = { ...filters };
    if (supplierId === null) {
      newFilters.supplierIds = null;
    }
    if (supplierProductCategoryKey !== undefined) {
      newFilters.supplierProductCategoryKey = supplierProductCategoryKey;
    }
    setFilters(newFilters);
  };

  return (
    <ProductSearchContentListHeader
      edgeBleed={edgeBleed}
      initialFilterState={initialFilterState}
      filters={filters}
      setFilters={setFilters}
      resetFilters={resetFilters}
      hideFilters={section !== "results"}
      productSearchTypeOptions={productSearchTypeOptions}
      productSearchType={productSearchType}
      setProductSearchType={setProductSearchType}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      activeView={activeView}
      setActiveView={setActiveView}
      allowedViews={allowedViews}
      productSearchVariables={productSearchVariables}
      handleClose={handleClose}
      handleProductCreate={handleProductCreate}
      handleSupplierCatalogueProductCreate={
        handleProductChoice
          ? async (product, handleClose) => {
              await handleProductChoice(product, filters);
              handleClose();
            }
          : undefined
      }
      canChangeProductType={canChangeProductType}
      includePreferences={includePreferences}
      searchEnabled={searchEnabled}
      setSearchEnabled={setSearchEnabled}
      handleBackClick={handleBackClick}
      handleSearchSubmit={handleSearchSubmit}
      onProductSelect={product =>
        setProductOverviewModalProduct({
          articleNumber: product.articleNumber,
          supplierId: product.supplierId,
        })
      }
      AdditionalChips={
        !isMinTablet &&
        showCategoriesSelect &&
        productCategoryProps.supplier ? (
          <ProductCategoryTreeChip
            supplier={productCategoryProps.supplier}
            productCategory={productCategoryProps.productCategory}
            handleSelect={handleCategoryTreeSelect}
          />
        ) : undefined
      }
    >
      {({ SearchLine, FiltersLine, ChipsLine, SearchBackdrop }) => (
        <Stack
          direction={"column"}
          spacing={1}
          sx={{
            height: "100%",
          }}
        >
          <Box>
            <Box
              sx={{
                overflow: "hidden",
                pb: 1,
                mb: -1,
                width: "100%",
                ...(edgeBleed
                  ? {
                      px: { xs: 1, md: 1.5 },
                      mx: { xs: -1, md: -1.5 },
                      boxSizing: "content-box",
                    }
                  : undefined),
              }}
            >
              {SearchLine}
            </Box>
          </Box>
          {SearchBackdrop}
          {query.error && <ErrorMessage message={query.error.message} />}
          {section === "results" ? (
            <LayoutWithLeftColumn
              name="ProductSearchModal"
              leftColumn={
                showCategoriesSelect ? (
                  <ProductCategoryTreeSelect
                    {...productCategoryProps}
                    categoryKey={filters.supplierProductCategoryKey ?? null}
                    onSelect={handleCategoryTreeSelect}
                  />
                ) : undefined
              }
              spacing={0}
              leftColumnWidth={isMinLargeDesktop ? "xs" : "xxs"}
            >
              <Stack
                direction={"column"}
                spacing={1}
                sx={{
                  height: "100%",
                  width: "100%",
                  minHeight: 0,
                  minWidth: 0,
                }}
              >
                {FiltersLine}
                {ChipsLine}
                <ProductsList
                  items={products}
                  totalCount={totalCount}
                  activeView={activeView}
                  loading={query.loading}
                  onSelectItem={setProductOverviewModalProduct}
                  paginationModel={paginationModel}
                  setPaginationModel={setPaginationModel}
                  pageSizeOptions={pageSizeOptions}
                  sortModel={sortModel}
                  setSortModel={setSortModel}
                  onProductSelect={onProductSelect}
                  productTypeInContext={getPreferenceProductType(
                    pinnedPreferenceProductTypeId,
                    filters
                  )}
                  isSelected={isSelectedProduct}
                />
              </Stack>
            </LayoutWithLeftColumn>
          ) : section === "landing" ? (
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
              }}
            >
              <ProductSearchLandingPage
                onProductClick={product =>
                  setProductOverviewModalProduct(product)
                }
                renderProductSelectButton={
                  onProductSelect
                    ? product => (
                        <ButtonCircleWrapper>
                          <ProductSelectButton
                            product={product}
                            onProductSelect={onProductSelect}
                          />
                        </ButtonCircleWrapper>
                      )
                    : undefined
                }
                setProductSearchFilters={setFilters}
              />
            </Box>
          ) : null}
          {productOverviewModalProduct && (
            <ProductOverviewModal
              productArticleNumber={productOverviewModalProduct.articleNumber}
              productSupplierId={productOverviewModalProduct.supplierId}
              handleSubmit={
                handleProductChoice
                  ? async product => {
                      track({
                        eventName: "AddProduct",
                        data: { product, variables: productSearchVariables },
                      });
                      await handleProductChoice(product, filters);
                      handleClose();
                    }
                  : undefined
              }
              handleClose={() => setProductOverviewModalProduct(null)}
            />
          )}
        </Stack>
      )}
    </ProductSearchContentListHeader>
  );
};

function ProductSearchContentListHeader({
  handleProductCreate,
  handleSupplierCatalogueProductCreate,
  handleClose,
  children,
  edgeBleed,
  ...props
}: Omit<
  React.ComponentProps<typeof ProductSearchListHeader>,
  "ActionButtons" | "children"
> & {
  handleProductCreate?: ProductCreateModalProps["handleSubmit"];
  handleSupplierCatalogueProductCreate?: SupplierCatalogueProductCreateModalProps["handleComplete"];
  handleClose: () => void;
  children: ChildrenFn;
  edgeBleed: boolean;
}) {
  const { t } = useTranslate(["Global", "ProductSearch"]);

  const [initialSupplierCatalogue, setInitialSupplierCatalogue] =
    React.useState<SupplierCatalogueValue | null>(null);

  const { canCreateSupplierCatalogueProduct } =
    useCanCreateSupplierCatalogueProduct();

  const productCreateRef = React.useRef<ModalOpenProcessRef>();
  const supplierCatalogueProductCreateRef = React.useRef<ModalOpenProcessRef>();
  const productAskTypeProcessRef =
    React.useRef<ProductAskTypeProcessRef | null>(null);

  const handleCreateNewProduct = async () => {
    if (
      handleProductCreate &&
      handleSupplierCatalogueProductCreate &&
      canCreateSupplierCatalogueProduct
    ) {
      // both possible - need to ask at first which product to create
      const values = await productAskTypeProcessRef.current?.open();
      if (values) {
        if (values.productType === "one-time") {
          productCreateRef.current?.open();
        } else if (values.productType === "catalogue") {
          setInitialSupplierCatalogue(values.supplierCatalogue || null);
          setTimeout(() => {
            supplierCatalogueProductCreateRef.current?.open();
          });
        }
      }
    } else if (handleProductCreate) {
      // one-time used product
      productCreateRef.current?.open();
    } else if (
      handleSupplierCatalogueProductCreate &&
      canCreateSupplierCatalogueProduct
    ) {
      // supplier catalogue product
      setInitialSupplierCatalogue(null);
      setTimeout(() => {
        supplierCatalogueProductCreateRef.current?.open();
      });
    }
  };

  const ActionButtons =
    handleProductCreate ||
    (handleSupplierCatalogueProductCreate &&
      canCreateSupplierCatalogueProduct) ? (
      <ButtonCreate
        title={t("New product", { ns: "ProductSearch" })}
        onClick={handleCreateNewProduct}
      />
    ) : undefined;

  return (
    <>
      <ProductSearchListHeader
        {...props}
        children={children}
        ActionButtons={ActionButtons}
        px={edgeBleed ? { xs: 1, md: 1.5 } : undefined}
        mx={edgeBleed ? { xs: -1, md: -1.5 } : undefined}
        pt={edgeBleed ? 1 : undefined}
        mt={edgeBleed ? -1 : undefined}
        onProductSelect={props.onProductSelect}
      />
      {handleProductCreate && (
        <ModalOpenProcess
          ref={productCreateRef}
          Modal={ProductCreateModal}
          modalProps={{
            handleSubmit: async values => {
              await handleProductCreate(values);
              handleClose();
            },
          }}
        />
      )}
      {handleSupplierCatalogueProductCreate &&
        canCreateSupplierCatalogueProduct && (
          <ModalOpenProcess
            ref={supplierCatalogueProductCreateRef}
            Modal={SupplierCatalogueProductCreateModal}
            modalProps={{
              initialSupplierCatalogue,
              handleComplete: handleSupplierCatalogueProductCreate,
            }}
          />
        )}
      <ProductAskTypeProcess ref={productAskTypeProcessRef} />
    </>
  );
}

function ProductSelectButton({
  product,
  onProductSelect,
}: {
  product: ProductSearchItem__ProductSearchResultFragment;
  onProductSelect(
    product: ProductSearchItem__ProductSearchResultFragment
  ): void | Promise<void>;
}) {
  const { t } = useTranslate(["Global", "ProductSearch"]);
  const [processing, run] = useRunWithFlag();
  return (
    <IconButton
      color={"primary"}
      size={"small"}
      disabled={processing}
      onClick={async () => {
        await run(async () => {
          await onProductSelect(product);
        });
      }}
      title={t("Select", { ns: "ProductSearch" })}
      aria-label={t("Select", { ns: "ProductSearch" })}
    >
      <TouchAppIcon fontSize="small" />
    </IconButton>
  );
}

function ProductsList({
  items,
  totalCount,
  activeView,
  loading,
  onSelectItem,
  paginationModel,
  setPaginationModel,
  pageSizeOptions,
  sortModel,
  setSortModel,
  onProductSelect,
  productTypeInContext,
  isSelected,
}: {
  items: ProductSearchItem__ProductSearchResultFragment[];
  totalCount: number;
  activeView: CollectionView;
  loading: boolean;
  onSelectItem(product: ProductSearchItem__ProductSearchResultFragment): void;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: (newPaginationModel: {
    page: number;
    pageSize: number;
  }) => void;
  pageSizeOptions?: number[];
  sortModel: PimSearchProductsSorting[];
  setSortModel: (model: PimSearchProductsSorting[]) => void;
  onProductSelect?: (
    product: ProductSearchItem__ProductSearchResultFragment
  ) => void | Promise<void>;
  productTypeInContext: string | null;
  isSelected(product: ProductSearchItem__ProductSearchResultFragment): boolean;
}) {
  const stateStore = useDataGridStateStore("ProductSearchModal");
  const preferences = useProductPreferences(
    productTypeInContext === null
      ? null
      : { productTypeId: productTypeInContext }
  );
  const ActionButton = React.useCallback(
    ({
      product,
    }: {
      product: ProductSearchItem__ProductSearchResultFragment;
    }) => (
      <>
        <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
          <ButtonCircleWrapper>
            <OrganisationSupplierProductFavouriteButton
              product={product}
              canEdit={true}
            />
          </ButtonCircleWrapper>
        </RestrictedByOrganisationPermissionWithDebug>
        {productTypeInContext ? (
          <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
            <ButtonCircleWrapper>
              <ProductPreferenceButton
                productTypeId={productTypeInContext}
                product={product}
                preferences={preferences}
              />
            </ButtonCircleWrapper>
          </RestrictedByOrganisationPermissionWithDebug>
        ) : null}
        {onProductSelect ? (
          <ButtonCircleWrapper>
            <ProductSelectButton
              product={product}
              onProductSelect={onProductSelect}
            />
          </ButtonCircleWrapper>
        ) : null}
      </>
    ),
    [onProductSelect, productTypeInContext, preferences]
  );
  return (
    <ProductsListView
      items={items}
      totalCount={totalCount}
      activeView={activeView}
      loading={loading}
      paginationModel={paginationModel}
      setPaginationModel={setPaginationModel}
      pageSizeOptions={pageSizeOptions}
      sortModel={sortModel}
      setSortModel={setSortModel}
      CardActionButton={ActionButton}
      TableActions={ActionButton}
      stateStore={stateStore}
      tableActionsWidth={
        70 + (productTypeInContext ? 40 : 0) + (onProductSelect ? 40 : 0)
      }
      onClick={onSelectItem}
      isSelected={isSelected}
    />
  );
}
